import { useEffect, useState, useRef } from 'react';
import styles from './ItemPage.module.css';
import { useParams } from 'react-router-dom';
import { useGetItemByIdQuery } from '../../store/api/item.api';
import { useGetUserWishlistQuery, useAddWishlistMutation, useDeleteWishlistItemMutation } from '../../store/api/wishlist.api';
import { useDbUserId } from '../../hooks/useDbUserId';
import { sendProductDetail } from '../../utils/yandexMetrika';

import { Header } from '../../components/Header/Header';
import { FormBtn } from '../../UI/FormBtn/FormBtn';
import { SubscBtn } from '../../UI/SubscBtn/SubscBtn';
import { BlueBtn } from '../../UI/BlueBtn/BlueBtn';

import wishlistIcon from '../../assets/wishlist-icon.svg';
import likeBlueIcon from '../../assets/like_blue-icon.svg';
import platformIcon from '../../assets/platform-icon.svg';
import langIcon from '../../assets/lang-icon.svg';
import turkishFlagIcon from '../../assets/turkish_flag-icon.svg';
import regionIcon from '../../assets/region-icon.svg';
import dateIcon from '../../assets/releaseDate-icon.png';

export const ItemPage = () => {
	const [editionBtnActive, setEditionBtnActive] = useState(0);
	const { id } = useParams();
	const { dbUserId } = useDbUserId();
	const schema = window.sessionStorage.getItem('schema');
	const hasSentDetail = useRef(false);

	const { data: item, isLoading, isError } = useGetItemByIdQuery({ itemId: id, schema });

	const [itemPrice, setItemPrice] = useState({
		id: null,
		price: null,
		price_disc: null,
		price_disc_sub: null,
		disc_end_date: null,
	});

	// дефолтно выбираем самое дешёвое издание/игру с бэка
	useEffect(() => {
		if (item) {
			if (item.min_edition) {
				// минимальное издание
				setItemPrice({
					id: item.min_edition.id,
					price: item.min_edition.price,
					price_disc: item.min_edition.price_disc || null,
					disc_end_date: item.min_edition.disc_end_date || null,
				});
				const index = item.editions.findIndex(e => e.id === item.min_edition.id);
				setEditionBtnActive(index + 1); // +1 потому что 0 = сама игра
			} else {
				// минимальная цена у самой игры
				setItemPrice({
					id: null,
					price: item.price,
					price_disc: item.price_disc,
					price_disc_sub: item.price_disc_sub,
					disc_end_date: item.disc_end_date,
				});
				setEditionBtnActive(0);
			}
		}
	}, [item]);

	// метрика
	useEffect(() => {
		if (
			item &&
			itemPrice.price &&
			!hasSentDetail.current
		) {
			sendProductDetail(item, schema);
			hasSentDetail.current = true;
		}
	}, [item, itemPrice]);

	const { data: wishlistData, refetch } = useGetUserWishlistQuery(
		{ userId: dbUserId, schema },
		{ skip: isLoading || !dbUserId || schema === 'steam' }
	);
	const [addItemToWishlist] = useAddWishlistMutation();
	const [deleteItemFromWishlist] = useDeleteWishlistItemMutation();

	const [inWishlist, setInWishlist] = useState(false);

	useEffect(() => {
		if (wishlistData?.items && id) {
			const exists = wishlistData.items.some(wishlistItem => wishlistItem.itemId === Number(id));
			setInWishlist(exists);
		}
	}, [wishlistData, id]);

	const handleEditionClick = (index, edition) => {
		if (!edition) {
			setItemPrice({
				id: null,
				price: item.price,
				price_disc: item.price_disc || null,
				price_disc_sub: item.price_disc_sub || null,
				disc_end_date: item.disc_end_date || null,
			});
		} else {
			setItemPrice({
				id: edition.id,
				price: edition.price,
				price_disc: edition.price_disc || null,
				disc_end_date: edition.disc_end_date || null,
			});
		}
		setEditionBtnActive(index);
	};

	const toggleWishlist = async () => {
		if (!dbUserId) return;

		try {
			if (inWishlist) {
				await deleteItemFromWishlist({ data: { userId: dbUserId, itemId: id }, schema });
				setInWishlist(false);
			} else {
				await addItemToWishlist({ data: { userId: dbUserId, itemId: id }, schema });
				setInWishlist(true);
			}
			await refetch();
		} catch (error) {
			console.error('Ошибка при изменении wishlist:', error);
		}
	};

	const calculateDaysLeft = (endDate) => {
		const now = new Date();
		const end = new Date(endDate);
		return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
	};

	if (isLoading) return <div>Загрузка...</div>;
	if (isError || !item) return <div>Произошла ошибка или товар не найден.</div>;

	const discountEndDate = itemPrice.disc_end_date ? calculateDaysLeft(itemPrice.disc_end_date) : null;
	const genres = schema !== '' ? item?.regions?.title : item?.genres?.map(g => g.title).join(', ');

	const region = schema === 'steam' ? item?.regions.map(reg => reg.title).join(', ') || '' : schema === 'steam' ? 'Турция' : item?.regions?.title;

	return (
		<div className={'main'}>
			<div
				className={styles.itemImg}
				style={{
					backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent), url(${item.img})`
				}}
			>
				<Header />
			</div>

			<div className={styles.title_block}>
				<div className={styles.text_block}>
					<h2>{item.title}</h2>
					{discountEndDate !== null && discountEndDate >= 0 && (
						<p>Скидка закончится через {discountEndDate} д.</p>
					)}
				</div>
				{
					schema !== 'steam' &&
					<img
						src={inWishlist ? likeBlueIcon : wishlistIcon}
						alt="wishlist"
						onClick={toggleWishlist}
					/>
				}
			</div>

			{schema === '' && (
				<div className="form">
					<p>Издания</p>
					<FormBtn
						key={0}
						text={item.title}
						underText={`${item.price_disc || item.price} ₽`}
						active={editionBtnActive === 0}
						onClick={() => handleEditionClick(0)}
					/>
					{item.editions?.map((edition, index) => (
						<FormBtn
							key={index + 1}
							text={edition.title}
							underText={`${edition.price_disc || edition.price} ₽`}
							active={editionBtnActive === index + 1}
							onClick={() => handleEditionClick(index + 1, edition)}
						/>
					))}
				</div>
			)}

			{itemPrice.price_disc_sub && (
				<div className={styles.sub_block}>
					<SubscBtn
						title={item.subscription?.title}
						sub={item.subscription?.category}
						price={itemPrice.price_disc_sub}
					/>
				</div>
			)}

			<div className={styles.about}>
				<div className={styles.about_block}>
					<img src={schema === '' ? turkishFlagIcon : regionIcon} alt="region" />
					<div>
						<p>Регион</p>
						<p>{region}</p>
					</div>
				</div>

				{
					schema !== 'steam' &&
					<>
						<div className={styles.about_block}>
							<img src={platformIcon} alt="platform" />
							<div>
								<p>Платформа</p>
								<p>{item.devices?.map((device, i) => `${device.name}${i < item.devices.length - 1 ? ', ' : ''}`)}</p>
							</div>
						</div>

						{
							item.releaseDate &&
							<div className={styles.about_block}>
								<img src={dateIcon} alt="dateIcon" width={22} height={22} style={{ marginRight: 2 }} />
								<div>
									<p>Дата релиза</p>
									<p>{new Date(item.releaseDate).toLocaleDateString("ru-RU")}</p>
								</div>
							</div>
						}
					</>
				}

				{schema === '' && (
					<>
						<div className={styles.about_block}>
							<img src={langIcon} alt="lang" />
							<div>
								<p>Локализация</p>
								<p>{item.lang}</p>
							</div>
						</div>
						<div className={styles.about_block}>
							<img src={platformIcon} alt="genre" />
							<div>
								<p>Жанр</p>
								<p>{genres}</p>
							</div>
						</div>
					</>
				)}
			</div>

			<div className={styles.price_block}>
				{(itemPrice.price_disc || itemPrice.price_disc_sub) && (
					<span className={styles.price_amount}>{itemPrice.price} ₽</span>
				)}

				<h2 style={itemPrice.price_disc || itemPrice.price_disc_sub ? { color: 'red' } : {}}>
					{itemPrice.price_disc_sub
						? `${itemPrice.price_disc_sub} ₽`
						: itemPrice.price_disc
							? `${itemPrice.price_disc} ₽`
							: `${itemPrice.price} ₽`}
				</h2>

				<div className={styles.fix_block}>
					<BlueBtn
						type='inItem'
						text="Купить"
						itemId={itemPrice.id ? null : item.id}
						editionId={itemPrice.id}
					/>
				</div>

			</div>
		</div>
	);
};
